import React from 'react';
import { Check, X, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const TrueFalseNotGivenQuestion = ({
  question,
  result,
  currentAnswer,
  submittedFirstTime,
  showExplanation,
  canRetry, // This will always be false for true/false questions
  onAnswerChange,
  onToggleExplanation
}) => {
  // Determine if this is a 2-option (True/False) or 3-option (True/False/Not Given) question
  const isThreeOption = question.question_type === "true_false_not_given";

  const options = isThreeOption
    ? [
      { value: 'TRUE', label: 'True', icon: Check, color: 'text-green-600' },
      { value: 'FALSE', label: 'False', icon: X, color: 'text-red-600' },
      { value: 'NOT GIVEN', label: 'Not Given', icon: HelpCircle, color: 'text-gray-600' }
    ]
    : [
      { value: 'TRUE', label: 'True', icon: Check, color: 'text-green-600' },
      { value: 'FALSE', label: 'False', icon: X, color: 'text-red-600' }
    ];

  const handleOptionSelect = (value) => {
    // Only allow changes if not submitted first time (no retry allowed for true/false questions)
    if (!submittedFirstTime) {
      onAnswerChange(value);
    }
  };

  const getOptionStyle = (optionValue) => {
    const isSelected = currentAnswer === optionValue;
    const isDisabled = submittedFirstTime; // Always disabled after first submission

    let baseClasses = "flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ";

    if (isDisabled) {
      baseClasses += "cursor-not-allowed opacity-60 ";
    }

    if (isSelected) {
      if (result?.isCorrect && submittedFirstTime) {
        baseClasses += "border-green-500 bg-green-50 text-green-800 ";
      } else if (submittedFirstTime) {
        baseClasses += "border-red-500 bg-red-50 text-red-800 ";
      } else {
        baseClasses += "border-blue-500 bg-blue-50 text-blue-800 ";
      }
    } else {
      baseClasses += "border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 ";
    }

    return baseClasses;
  };

  const getResultIcon = () => {
    if (!submittedFirstTime || !result) return null;

    if (result.isCorrect) {
      return <Check className="w-5 h-5 text-green-600 ml-2" />;
    } else {
      return <X className="w-5 h-5 text-red-600 ml-2" />;
    }
  };

  const getQuestionStatus = () => {
    if (!submittedFirstTime) return 'unanswered';
    if (result?.isCorrect) return 'correct';
    return 'incorrect-final'; // Always final for true/false questions
  };

  const getQuestionHeaderStyle = () => {
    const status = getQuestionStatus();
    switch (status) {
      case 'correct':
        return 'border-l-4 border-green-500 bg-green-50';
      case 'incorrect-final':
        return 'border-l-4 border-red-500 bg-red-50';
      default:
        return 'border-l-4 border-gray-300 bg-white';
    }
  };

  // Helper function to get the actual answer user selected
  const getActualUserAnswer = () => {
    // Always show the answer that was actually selected by the user
    if (currentAnswer !== undefined && currentAnswer !== '') {
      return currentAnswer;
    }

    // If no current answer but we know it was submitted, show a fallback
    if (submittedFirstTime) {
      return 'Answer not recorded properly';
    }

    return 'No answer selected';
  };

  // Helper function to get feedback text
  const getFeedbackText = () => {
    if (!question.feedback) return null;

    // If feedback is a string, return it directly
    if (typeof question.feedback === 'string') {
      return question.feedback;
    }

    // If feedback is an object, try to get the appropriate feedback
    if (typeof question.feedback === 'object') {
      // First try to get feedback based on result status
      if (result?.isCorrect && question.feedback.correct) {
        return question.feedback.correct;
      }

      // For incorrect answers, check if feedback.incorrect is an object with user's answer
      if (!result?.isCorrect && question.feedback.incorrect) {
        // If incorrect feedback is an object, get feedback for user's current answer
        if (typeof question.feedback.incorrect === 'object') {
          // If we have currentAnswer, use it
          if (currentAnswer && question.feedback.incorrect[currentAnswer]) {
            return question.feedback.incorrect[currentAnswer];
          }

          // If currentAnswer is undefined but we know it's incorrect, 
          // we need to find which incorrect option has feedback
          // Since the correct answer is known, show feedback for any available incorrect option
          const incorrectOptions = Object.keys(question.feedback.incorrect);
          if (incorrectOptions.length > 0) {
            // Return the first available incorrect feedback
            return question.feedback.incorrect[incorrectOptions[0]];
          }
        }
        // If incorrect feedback is a string
        if (typeof question.feedback.incorrect === 'string') {
          return question.feedback.incorrect;
        }
      }

      // Try to get feedback based on current answer directly
      if (currentAnswer && question.feedback[currentAnswer]) {
        return question.feedback[currentAnswer];
      }

      // If feedback is structured directly by answer options (fallback)
      const correctAnswer = question.answer;
      if (question.feedback[correctAnswer]) {
        return question.feedback[correctAnswer];
      }

      // Try common keys for direct answer feedback
      if (question.feedback.TRUE) return question.feedback.TRUE;
      if (question.feedback.FALSE) return question.feedback.FALSE;
      if (question.feedback.NOT_GIVEN || question.feedback['NOT GIVEN']) {
        return question.feedback.NOT_GIVEN || question.feedback['NOT GIVEN'];
      }

      // If it's an object but we can't determine the structure, 
      // return a generic message
      return "Please review the correct answer and explanation.";
    }

    return "No feedback available.";
  };

  return (
    <div className={`border rounded-lg p-4 transition-colors ${!submittedFirstTime ? 'bg-gray-50 border-gray-200 hover:bg-gray-100' :
      result?.isCorrect ? 'bg-green-50 border-green-200' :
        'bg-red-50 border-red-200'
      }`}>
      {/* Question Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <p className="font-medium text-gray-800 leading-relaxed">
            {question.question_number}. {question.question_text || question.question}
          </p>

          <span className="text-xs text-gray-500 mt-1 block">
            {isThreeOption ? "True / False / Not Given" : "True / False"}
          </span>

        </div>

        <div className="flex items-center ml-4 space-x-2">
          {submittedFirstTime && (
            <span className={`text-xs px-2 py-1 rounded-full ${result?.isCorrect ? 'bg-green-200 text-green-700' : 'bg-red-200 text-red-700'
              }`}>
              {result?.isCorrect ? '✓ Correct' : '✗ Incorrect'}
            </span>
          )}
          {submittedFirstTime && question.feedback && (
            <button
              onClick={onToggleExplanation}
              className="text-xs px-2 py-1 rounded-full bg-blue-200 text-blue-700 hover:bg-blue-300 transition-colors flex items-center space-x-1"
            >
              <HelpCircle className="w-3 h-3" />
              <span>Explanation</span>
            </button>
          )}
        </div>
      </div>

      {/* Explanation Content - Show only user answer and explanation */}
      {showExplanation && submittedFirstTime && question.feedback && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm space-y-2">
            <p><strong>Your answer:</strong> {getActualUserAnswer()}</p>
            <div className="pt-2 border-t border-blue-200">
              <p><strong>Explanation:</strong></p>
              <p className="text-gray-700">
                {getFeedbackText()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Options */}
      <div className="space-y-2 mb-4">
        {options.map((option) => {
          const OptionIcon = option.icon;
          const isSelected = currentAnswer === option.value;
          const isDisabled = submittedFirstTime; // Always disabled after submission

          return (
            <label
              key={option.value}
              className={`flex items-start space-x-3 cursor-pointer p-3 rounded-lg transition-all ${isDisabled
                ? 'bg-gray-100 border border-gray-200 cursor-not-allowed opacity-60'
                : isSelected
                  ? 'bg-blue-100 border border-blue-300'
                  : 'bg-white border border-gray-200 hover:bg-blue-50 hover:border-blue-200'
                }`}
            >
              <input
                type="radio"
                name={`q${question.question_number}`}
                value={option.value}
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => !isDisabled && onAnswerChange(option.value)}
                className="text-blue-600 mt-0.5 flex-shrink-0 disabled:opacity-50"
              />
              <div className="flex items-center">
                <OptionIcon className={`w-4 h-4 ${option.color} mr-2`} />
                <span className={`text-sm leading-relaxed font-medium ${isSelected ? 'text-blue-800' : 'text-gray-700'
                  } ${isDisabled ? 'text-gray-500' : ''}`}>
                  {option.label}
                </span>
              </div>
            </label>
          );
        })}
      </div>

      {/* Correct answer display removed for True/False questions */}

      {/* No retry message for True/False questions - removed this section completely */}

      {/* Score display */}
      {submittedFirstTime && result && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Score:</span>
            <span className={`font-semibold ${result.score > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {result.score}/1
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrueFalseNotGivenQuestion;